import React from 'react';
import KPIWrapper from '../KPIWrapper/KPIWrapper';
import delve from 'dlv';
import { compose, withProps } from 'recompose';
import { PromiseState } from 'react-refetch';

import { ReactComponent as DownloadTrackIcon } from 'icons/topics/download-track.svg';
import { ReactComponent as DownloadReleaseIcon } from 'icons/topics/download-release.svg';
import KeyValueGroup from 'components/base/KeyValueGroup/KeyValueGroup';
import KPILoader from 'components/utility/ContentLoader/KPILoader/KPILoader';
import capitalize from 'lodash.capitalize';

import mapKPIData from '../mappings/mapKPIData';
import apiConnect from 'helpers/apiConnect';
import stringToSlug from 'helpers/stringToSlug';
import formatDate from 'helpers/formatDate';
import useDateRangeKPI from 'hooks/useDateRangeKPI';
import useMedia from 'hooks/useMedia';

import {
  Container,
  KPIList,
  KPIListItem,
  Header,
  TWTDText,
  DateRange,
} from '../shared';

import { withEntityContext } from 'context/entityContext';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import getDownloadKPIURL from 'url-constructors/getDownloadKPIURL';
import useExportURL from 'hooks/useExportURL';
import { EntityTypes } from 'context/entityContext';
import { IDownloadEntityData } from 'components/data-containers/KPIs/types';
import { tooltipContent } from 'helpers/getTooltipContent';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import { IToolbarItemType } from 'components/utility/GenericComponent/GenericComponent';

interface IDownloadFetchData {
  entity: {
    id: string;
    type: EntityTypes;
  };
  track_data: IDownloadEntityData;
  album_data: IDownloadEntityData;
}
interface IDownloadKPIs {
  compareMode: string;
  dataColors: string[];
  entityType: EntityTypes;
  dataFetch: PromiseState<IDownloadFetchData[]>;
  error: any;
  countryIds: string[];
  entityIds: string[];
}

const ALLOWED_FILTERS = [
  'country',
  'download',
  'media',
  'region',
  'customRegion',
];

const GLOSSARY_SLUG = 'kpis/download-kpis';

const DownloadKPIs: React.FC<IDownloadKPIs> = (props) => {
  const {
    compareMode,
    dataColors,
    entityType,
    dataFetch = PromiseState.create(),
    error,
    countryIds,
    entityIds,
  } = props;

  const isMobileView = useMedia('(max-width: 40em)');

  const [thisWeekToDate, lastWeek] = useDateRangeKPI('downloads');

  const showSplit = ['artist', 'project'].includes(props.entityType);

  const compareArray = compareMode === 'country' ? countryIds : entityIds;

  const primaryData = mapKPIData({
    data: dataFetch.value,
    split: showSplit ? 'track' : entityType,
    compareMode,
    compareArray,
  });

  const secondaryData =
    showSplit &&
    mapKPIData({
      data: dataFetch.value,
      split: 'release',
      compareMode,
      compareArray,
    });

  const exportUrl = useExportURL(dataFetch);

  return (
    <Container
      data-pw={stringToSlug(
        `Download ${showSplit ? 'Track' : capitalize(entityType)} KPIs`
      )}
    >
      <ErrorBoundary error={error}>
        <KPIWrapper
          title={`Download ${
            showSplit ? 'Track' : capitalize(entityType)
          } KPIs`}
          icon={DownloadTrackIcon}
          allowedFilters={ALLOWED_FILTERS}
          glossarySlug={GLOSSARY_SLUG}
          extraToolbarItems={[
            {
              label: 'Export Download KPI data',
              href: exportUrl,
              type: IToolbarItemType.Export,
              order: 1,
            },
          ]}
          titleAlignLeft="-1.5em"
          alignSVG="2em"
        >
          <KPIList marginTop="26px">
            <KPIListItem>
              <Header>
                <TippyToolTip
                  enabled
                  tooltipContent={tooltipContent}
                  offset={[10, 25]}
                  maxWidth="170px"
                >
                  <TWTDText>{`This Week ${
                    !isMobileView ? 'to Date' : ''
                  }`}</TWTDText>
                </TippyToolTip>
                {thisWeekToDate && (
                  <DateRange>
                    {`${formatDate(thisWeekToDate?.startDate, 'day', true)} 
                    - ${formatDate(thisWeekToDate?.endDate, 'day', true)}`}
                  </DateRange>
                )}
              </Header>
              {!primaryData && <KPILoader showPrevious showChange />}
              {primaryData && (
                <KeyValueGroup
                  items={[
                    {
                      label: 'Downloads',
                      metric: delve(primaryData, 'count_this_week_to_date'),
                      cy: 'download-kpi-downloads-twtd',
                    },
                    {
                      label: 'Previous',
                      metric: delve(primaryData, 'count_last_week_to_date'),
                      cy: 'download-kpi-previous-twtd',
                    },
                    {
                      label: 'Change',
                      metric: delve(primaryData, 'wow_in_progress'),
                      cy: 'download-kpi-change-twtd',
                    },
                  ]}
                  dataColors={dataColors}
                  compareMode={compareMode}
                  showCyAttr
                  isKPIValue
                />
              )}
            </KPIListItem>
            <KPIListItem>
              <Header>
                <TWTDText>
                  {`Last ${!isMobileView ? 'Full ' : ''}Week`}
                </TWTDText>

                {lastWeek && (
                  <DateRange>
                    {`${formatDate(lastWeek?.startDate, 'day', true)} 
                    - ${formatDate(lastWeek?.endDate, 'day', true)}`}
                  </DateRange>
                )}
              </Header>
              {!primaryData && <KPILoader showPrevious showChange />}
              {primaryData && (
                <KeyValueGroup
                  items={[
                    {
                      label: 'Downloads',
                      metric: delve(primaryData, 'count_last_week'),
                      cy: 'download-kpi-downloads-lwtd',
                    },
                    {
                      label: 'Previous',
                      metric: delve(primaryData, 'count_last_last_week'),
                      cy: 'download-kpi-previous-lwtd',
                    },
                    {
                      label: 'Change',
                      metric: delve(primaryData, 'wow_last_completed'),
                      cy: 'download-kpi-change-lwtd',
                    },
                  ]}
                  dataColors={dataColors}
                  compareMode={compareMode}
                  isKPIValue
                />
              )}
            </KPIListItem>
            <KPIListItem>
              <Header>Release to date</Header>
              {!primaryData && <KPILoader />}
              {primaryData && (
                <KeyValueGroup
                  items={[
                    {
                      label: 'Downloads',
                      metric: delve(primaryData, 'count_rtd'),
                      cy: 'download-kpi-downloads-rtd',
                    },
                  ]}
                  dataColors={dataColors}
                  compareMode={compareMode}
                />
              )}
            </KPIListItem>
          </KPIList>
        </KPIWrapper>

        {showSplit && (
          <KPIWrapper
            title="Download Release KPIs"
            icon={DownloadReleaseIcon}
            allowedFilters={ALLOWED_FILTERS}
            glossarySlug={GLOSSARY_SLUG}
            extraToolbarItems={[
              {
                label: 'Export Download KPI data',
                href: exportUrl,
                type: IToolbarItemType.Export,
                order: 1,
              },
            ]}
            titleAlignLeft="-1.5em"
            alignSVG="2em"
          >
            <KPIList>
              <KPIListItem>
                <Header>
                  <TippyToolTip
                    enabled
                    tooltipContent={tooltipContent}
                    offset={[10, 25]}
                    maxWidth="170px"
                  >
                    <TWTDText>{`This Week ${
                      !isMobileView ? 'to Date' : ''
                    }`}</TWTDText>
                  </TippyToolTip>
                  {thisWeekToDate && (
                    <DateRange>
                      {`${formatDate(thisWeekToDate?.startDate, 'day', true)} 
                    - ${formatDate(thisWeekToDate?.endDate, 'day', true)}`}
                    </DateRange>
                  )}
                </Header>
                {!secondaryData && <KPILoader showPrevious showChange />}
                {secondaryData && (
                  <KeyValueGroup
                    items={[
                      {
                        label: 'Downloads',
                        metric: delve(secondaryData, 'count_this_week_to_date'),
                      },
                      {
                        label: 'Previous',
                        metric: delve(secondaryData, 'count_last_week_to_date'),
                      },
                      {
                        label: 'Change',
                        metric: delve(secondaryData, 'wow_in_progress'),
                      },
                    ]}
                    dataColors={dataColors}
                    compareMode={compareMode}
                    isKPIValue
                  />
                )}
              </KPIListItem>
              <KPIListItem>
                <Header>
                  <TWTDText>
                    {`Last ${!isMobileView ? 'Full ' : ''}Week`}
                  </TWTDText>

                  {lastWeek && (
                    <DateRange>
                      {`${formatDate(lastWeek?.startDate, 'day', true)} 
                      - ${formatDate(lastWeek?.endDate, 'day', true)}`}
                    </DateRange>
                  )}
                </Header>
                {!secondaryData && <KPILoader showPrevious showChange />}
                {secondaryData && (
                  <KeyValueGroup
                    items={[
                      {
                        label: 'Downloads',
                        metric: delve(secondaryData, 'count_last_week'),
                      },
                      {
                        label: 'Previous',
                        metric: delve(secondaryData, 'count_last_last_week'),
                      },
                      {
                        label: 'Change',
                        metric: delve(secondaryData, 'wow_last_completed'),
                      },
                    ]}
                    dataColors={dataColors}
                    compareMode={compareMode}
                    isKPIValue
                  />
                )}
              </KPIListItem>
              <KPIListItem>
                <Header>Release to date</Header>
                {!secondaryData && <KPILoader />}
                {secondaryData && (
                  <KeyValueGroup
                    items={[
                      {
                        label: 'Downloads',
                        metric: delve(secondaryData, 'count_rtd'),
                      },
                    ]}
                    dataColors={dataColors}
                    compareMode={compareMode}
                  />
                )}
              </KPIListItem>
            </KPIList>
          </KPIWrapper>
        )}
      </ErrorBoundary>
    </Container>
  );
};

export default compose(
  withEntityContext,
  apiConnect(
    ({
      entityType,
      entityIds,
      filters,
      compareMode,
      countryIds,
      customRegionFilter,
    }: any) => ({
      dataFetch: getDownloadKPIURL({
        entityType,
        entityIds,
        customerFilter: filters.download,
        mediaTypes: filters?.media,
        countryIds: compareMode === 'country' ? countryIds : filters.country,
        region: filters?.region,
        customRegion: customRegionFilter,
        compareMode,
      }),
    })
  ),
  withProps(
    ({ dataFetch }: { dataFetch: PromiseState<IDownloadFetchData> }) => {
      return {
        error: dataFetch.rejected
          ? new Error(delve(dataFetch, 'reason'))
          : null,
      };
    }
  )
)(DownloadKPIs as any) as any;
